import {
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps as MuiDialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Slide,
  Typography,
} from "@material-ui/core";
import MuiDialog from "@material-ui/core/Dialog";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Close } from "@material-ui/icons";
import { SlideTransition } from "components/Transition";
import { theme } from "config/theme";
import { COLORS, CSS_COLOR_VARS } from "constants/colors";
import useText from "constants/text";
import React, { ReactNode } from "react";
import "styled-components/macro";

export interface StylePropsDialogConfirm {
  height?: string;
}
const useStyles = makeStyles<Theme, StylePropsDialogConfirm>({
  paperWidthMd: {
    width: 740,
  },
  paperWidthLg: {
    width: 940,
  },
  dialogSpacingContent: {
    padding: "18px 28px",
  },
  paperDialogRoot: {
    position: "relative",
    width: "50%",
    boxShadow: "0px 15px 20px 3px rgba(0, 0, 0, 0.1)",
    height: "auto",
    [theme.breakpoints.down("xs")]: {
      width: "93% !important",
      top: -15,
      height: ({ height }) => height || "auto",
    },
  },
  dialogActionsContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  dialogActionsRoot: {
    marginLeft: "auto",
    padding: 0,
  },
  dialogActionsConfirmClose: {
    position: "absolute",
    bottom: 0,
    justifyContent: "flex-end",
    backgroundColor: CSS_COLOR_VARS.backgroundPrimary,
    pointerEvents: "auto",
    height: "100%",
    width: "100%",
    padding: "14px 28px",
    borderRadius: "4px",
    zIndex: theme.zIndex.modal + 1,
    boxSizing: "border-box",
  },
  bottoms: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    justifyItems: "end",
    [theme.breakpoints.down("xs")]: {
      marginLeft: "22px",
    },
  },
  bottomsStyle: {
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      position: "relative",
      top: -5,
      marginLeft: -3,
    },
  },
  buttonStyle: {
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      padding: "1px 0px 0px 40px",
    },
  },
  bottomInfo: {
    display: "flex",
    marginTop: "auto",
    marginBottom: "auto",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  closeBtnStyle: {
    marginLeft: "auto",
  },
  dialogTitle: {
    padding: "16px 12px 16px 24px",
  },
  phoneDialog: {
    [theme.breakpoints.down("xs")]: { marginTop: "100px", height: "90%" },
  },
  btnCancel: {
    width: "150px",
    marginLeft: -30,
    marginRight: "100px",
    backgroundColor: "#cfecf8",
    borderColor: "#399ebf",
    color: "#399ebf",
    [theme.breakpoints.down("xs")]: {
      height: "39px !important",
      width: "120px",
    },
  },
  btnConfirm: {
    marginLeft: -92,
    width: "150px",
    backgroundColor: "#399ebf",
    color: "#fff",
    [theme.breakpoints.down("xs")]: {
      height: "40px !important",
      width: "120px",
    },
  },
});

export interface ExtendedButtonProps extends ButtonProps {
  "data-testid"?: string;
}

export interface DialogProps extends MuiDialogProps {
  title?: string;
  subtitle?: string;
  customDialogTitle?: React.ReactNode;
  showBottomInfo?: boolean;
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  handleClose?(reason?: string): void;
  handleConfirm?(): void;
  disableConfirmButton?: boolean;
  PrimaryButtonProps?: ExtendedButtonProps;
  SecondaryButtonProps?: ExtendedButtonProps;
  isFormDirty?: boolean;
  hasStickyBars?: boolean;
  height?: string;
  bottomInfoView?: ReactNode | null;
  customBackground?: string;
  hideActions?: boolean;
  noPadding?: boolean;
  cancelButtonStyle?: string;
  footerStyle?: string;
  reponsiveHeight?: string;
}

export default function Dialog({
  title,
  subtitle,
  confirmButtonTitle,
  cancelButtonTitle,
  cancelButtonStyle,
  disableConfirmButton = false,
  handleConfirm,
  handleClose,
  PrimaryButtonProps = {},
  SecondaryButtonProps = {},
  isFormDirty = false,
  children,
  hasStickyBars = false,
  maxWidth = "sm",
  height,
  customDialogTitle,
  showBottomInfo = false,
  bottomInfoView = null,
  customBackground,
  hideActions = false,
  noPadding = false,
  footerStyle,
  reponsiveHeight = "auto",
  ...props
}: DialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles({ height: reponsiveHeight });
  const { text } = useText();
  const [showFooterActions, setShowFooterActions] = React.useState(false);
  const [, setTopSticky] = React.useState(false);
  const [, setBottomSticky] = React.useState(false);
  const dialogRef = React.useRef(null);

  const handleScroll = React.useCallback(() => {
    let throttleTimeout: any = null;

    function goesSticky() {
      const scrollEl = dialogRef?.current;
      const scrollTop = scrollEl?.scrollTop;

      const doesTopStick = window?.scrollY === scrollTop;
      const doesBottomStick = scrollTop + scrollEl?.clientHeight === scrollEl?.scrollHeight;

      setTopSticky(!doesTopStick);
      setBottomSticky(!doesBottomStick);

      throttleTimeout = null;
    }

    if (throttleTimeout === null) {
      throttleTimeout = setTimeout(goesSticky, 200);
    } else {
      goesSticky();
    }
  }, [setTopSticky]);

  function handleCloseDialog(event: any, reason?: string) {
    if (!isFormDirty) {
      handleClose(reason);
    } else {
      setShowFooterActions(true);
    }
  }

  React.useEffect(() => {
    if (hasStickyBars) {
      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("scroll", () => handleScroll, true);
      };
    }
  }, [handleScroll, hasStickyBars]);

  const onDiscard = () => {
    handleClose && handleClose();
    setShowFooterActions(false);
  };

  return (
    <MuiDialog
      key="dialog mui"
      fullWidth
      maxWidth={maxWidth}
      TransitionComponent={props.TransitionComponent || SlideTransition}
      fullScreen={fullScreen}
      onClose={(event, reason) => handleCloseDialog(event, reason)}
      scroll="paper"
      aria-labelledby={title?.toLowerCase()?.replace(/_/g, " ")}
      {...props}
      css={`
        > div > .MuiPaper-root {
          ${height && `height: ${fullScreen ? height : height}`};
          ${customBackground ?? customBackground}
          ${noPadding && `padding: 0 !important;`}
        }
      `}
      className={classes.phoneDialog}
      classes={{
        paperWidthMd: classes.paperWidthMd,
        paperWidthLg: classes.paperWidthLg,
        paper: classes.paperDialogRoot,
      }}
    >
      {customDialogTitle || (
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography
            variant="h3"
            css={`
              font-weight: 500;
              color: ${COLORS.primary};
            `}
          >
            {title}
          </Typography>
          <IconButton className={classes.closeBtnStyle} onClick={(event) => handleCloseDialog(event, "icon")}>
            <Close fontSize="small" color="action" />
          </IconButton>
        </DialogTitle>
      )}

      <DialogContent ref={dialogRef}>
        {subtitle && <DialogContentText>{subtitle}</DialogContentText>}
        {children}
      </DialogContent>

      {(handleClose || handleConfirm) && !hideActions && (
        <DialogActions>
          <Grid className={classes.bottoms}>
            {showBottomInfo && bottomInfoView && <div className={classes.bottomInfo}>{bottomInfoView}</div>}

            <Grid className={footerStyle || classes.bottomsStyle}>
              <Grid className={classes.buttonStyle} spacing={4}>
                {handleClose && (
                  <Button
                    variant="outlined"
                    onClick={handleCloseDialog}
                    {...SecondaryButtonProps}
                    className={classes.btnCancel}
                  >
                    {cancelButtonTitle ?? text.all.cancel}
                  </Button>
                )}

                {handleConfirm && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={disableConfirmButton}
                    onClick={handleConfirm}
                    {...PrimaryButtonProps}
                    className={classes.btnConfirm}
                    style={{
                      opacity: disableConfirmButton ? 0.5 : 1,
                    }}
                  >
                    {confirmButtonTitle ?? text.all.confirm}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      )}

      <Slide direction="up" in={showFooterActions && isFormDirty} mountOnEnter unmountOnExit>
        <Grid className={`${classes.dialogActionsContainer} ${classes.dialogActionsConfirmClose}`}>
          <Grid
            css={`
              max-width: 45%;
            `}
          >
            <span
              css={`
                color: ${CSS_COLOR_VARS.textGrayedOut};
              `}
            >
              {text.all.your_changes_wont_be}
            </span>
            . <b>{text.all.do_you_want_to_discard_them}</b>
          </Grid>
          <DialogActions className={classes.dialogActionsRoot}>
            <Button variant="outlined" onClick={() => setShowFooterActions(false)}>
              {text.all.keep_this_open}
            </Button>
            <Button variant="contained" color="secondary" onClick={onDiscard}>
              {text.all.discard_and_close}
            </Button>
          </DialogActions>
        </Grid>
      </Slide>
    </MuiDialog>
  );
}
